import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Settings, ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Filter, LogOut, User, Crown, 
    ChevronDown, X, Check, Clock, Users as UsersIcon, Eye, Palette, Repeat, Bell, Globe, Trash2, 
    Edit2, ExternalLink 
} from 'lucide-react';
import { fetchCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } from '../services/calendarEventsApi';
import { getGoogleCalendars } from '../services/googleCalendarApi';
import { fetchCalendarPreferences } from '../services/calendarPreferencesApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import UserAvatar from './UserAvatar';
import Premium from './Premium';
import CalendarSettings from './CalendarSettings';

const Calendar = () => {
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [eventTitle, setEventTitle] = useState('');
    const [location, setLocation] = useState('');
    const [isAllDay, setIsAllDay] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [startTime, setStartTime] = useState('16:25');
    const [endDate, setEndDate] = useState(new Date());
    const [endTime, setEndTime] = useState('17:25');
    const [defaultEventDuration, setDefaultEventDuration] = useState(30); // in minutes
    const [weekNumbersEnabled, setWeekNumbersEnabled] = useState(false);
    const [reminders, setReminders] = useState([{ value: 30, unit: 'minutes' }]);
    const [participants, setParticipants] = useState([]);
    const [visibleTo, setVisibleTo] = useState([]);
    const [eventColor, setEventColor] = useState('#1a73e8'); // Default blue
    const [repeatOption, setRepeatOption] = useState('none');
    const [repeatEndDate, setRepeatEndDate] = useState(null);
    const [description, setDescription] = useState('');
    const [timezone, setTimezone] = useState('Asia/Kolkata');
    const [showParticipantsModal, setShowParticipantsModal] = useState(false);
    const [showVisibilityModal, setShowVisibilityModal] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showRepeatModal, setShowRepeatModal] = useState(false);
    const [events, setEvents] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [googleCalendars, setGoogleCalendars] = useState([]);
    const [viewType, setViewType] = useState('agenda'); // 'day', '3-day', 'week', 'month', 'agenda'
    const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const viewDropdownRef = useRef(null);

    useEffect(() => {
        const loadFamilies = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Failed to load families');
                }

                const families = await response.json();
                setFamilies(families);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;

                if (selectedFamilyId && families.length > 0) {
                    familyToSelect = families.find(f => f.id === selectedFamilyId);
                }

                if (!familyToSelect && families.length === 1) {
                    familyToSelect = families[0];
                    localStorage.setItem('selectedFamilyId', families[0].id);
                }

                if (!familyToSelect && families.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }

                setSelectedFamily(familyToSelect);

                // Set default participants and visibility to all family members
                if (familyToSelect) {
                    const memberNames = familyToSelect.members
                        ?.filter(m => m && typeof m === 'object' && m.name)
                        .map(m => m.name) || [];
                    setParticipants(memberNames);
                    setVisibleTo(memberNames);
                }
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadFamilies();
    }, []);

    // Load default view and event duration from preferences
    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const prefs = await fetchCalendarPreferences();
                if (prefs.default_view) {
                    setViewType(prefs.default_view);
                }
                if (prefs.default_event_duration) {
                    setDefaultEventDuration(prefs.default_event_duration);
                }
                if (prefs.week_numbers_enabled !== undefined) {
                    setWeekNumbersEnabled(prefs.week_numbers_enabled);
                }
            } catch (error) {
                console.error('Error loading calendar preferences:', error);
                // Use defaults if loading fails
            }
        };
        loadPreferences();
    }, []);

    // Load events when family changes
    useEffect(() => {
        const loadEvents = async () => {
            if (!selectedFamily) return;

            try {
                const response = await fetchCalendarEvents(selectedFamily.id);
                setEvents(response.items || []);
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };
        loadEvents();
    }, [selectedFamily]);

    // Load Google Calendar integrations for styling
    useEffect(() => {
        const loadGoogleCalendars = async () => {
            if (!selectedFamily) return;
            try {
                const response = await getGoogleCalendars(selectedFamily.id);
                setGoogleCalendars(response.items || []);
            } catch (error) {
                console.error('Error loading Google Calendars:', error);
            }
        };
        loadGoogleCalendars();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target)) {
                setViewDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showAddEventModal || showPremiumModal || showSettings) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showAddEventModal, showPremiumModal, showSettings]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id.toString());
        setShowFamilyDropdown(false);
    };

    const formatDateForInput = (date) => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
    };

    // Gmail event colors
    const eventColors = [
        { name: 'Tomato', color: '#d50000' },
        { name: 'Flamingo', color: '#e67c73' },
        { name: 'Tangerine', color: '#f4511e' },
        { name: 'Banana', color: '#f6bf26' },
        { name: 'Sage', color: '#33b679' },
        { name: 'Basil', color: '#0b8043' },
        { name: 'Peacock', color: '#039be5' },
        { name: 'Blueberry', color: '#3f51b5' },
        { name: 'Lavender', color: '#7986cb' },
        { name: 'Grape', color: '#8e24aa' },
        { name: 'Graphite', color: '#616161' },
        { name: 'Default', color: '#1a73e8' }
    ];

    const timezones = [
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Asia/Dubai',
        'Asia/Kolkata',
        'Asia/Shanghai',
        'Asia/Tokyo',
        'Asia/Singapore',
        'Australia/Sydney',
        'Pacific/Auckland'
    ];

    const repeatOptions = [
        { value: 'none', label: 'Does not repeat' },
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
        { value: 'weekdays', label: 'Every weekday (Monday to Friday)' },
        { value: 'custom', label: 'Custom...' }
    ];

    const reminderUnits = [
        { value: 'minutes', label: 'minutes' },
        { value: 'hours', label: 'hours' },
        { value: 'days', label: 'days' },
        { value: 'weeks', label: 'weeks' }
    ];

    const handleSaveEvent = async () => {
        if (!eventTitle.trim()) {
            alert('Please enter event title');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const eventData = {
                title: eventTitle,
                description: description || null,
                location: location || null,
                start_date: startDate.toISOString().split('T')[0],
                start_time: isAllDay ? null : startTime,
                end_date: endDate.toISOString().split('T')[0],
                end_time: isAllDay ? null : endTime,
                all_day: isAllDay,
                timezone: timezone,
                participants: participants || [],
                visible_to: visibleTo || [],
                color: eventColor,
                repeat_pattern: repeatOption !== 'none' ? repeatOption : null,
                repeat_weekly_days: repeatOption === 'weekly' ? [1, 2, 3, 4, 5] : null, // Default to weekdays
                repeat_monthly_days: repeatOption === 'monthly' ? [startDate.getDate()] : null,
                repeat_end_date: repeatEndDate ? repeatEndDate : null,
                reminders: reminders.map(r => ({
                    type: 'notification',
                    time_before: `${r.value}_${r.unit}`
                })),
                notes: null
            };

            if (editingEvent) {
                const updatedEvent = await updateCalendarEvent(selectedFamily.id, editingEvent.id, eventData);
                setEvents(events.map(e => e.id === editingEvent.id ? updatedEvent : e));
                setEditingEvent(null);
            } else {
                const createdEvent = await createCalendarEvent(selectedFamily.id, eventData);
                setEvents([...events, createdEvent]);
            }

            resetEventForm();
            setShowAddEventModal(false);
        } catch (error) {
            console.error('Error saving event:', error);
            alert(`Failed to save event: ${error.message}`);
        }
    };

    // Helper function to calculate end time from start time + duration
    const calculateEndTime = (startTimeStr, durationMinutes) => {
        if (!startTimeStr || startTimeStr === '') return '';
        const [hours, minutes] = startTimeStr.split(':').map(Number);
        const startTotalMinutes = hours * 60 + minutes;
        const endTotalMinutes = startTotalMinutes + durationMinutes;
        const endHours = Math.floor(endTotalMinutes / 60) % 24;
        const endMins = endTotalMinutes % 60;
        return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    };

    const resetEventForm = () => {
        const defaultStartTime = '16:25';
        const calculatedEndTime = calculateEndTime(defaultStartTime, defaultEventDuration);
        
        setEventTitle('');
        setLocation('');
        setIsAllDay(false);
        setStartDate(new Date());
        setStartTime(defaultStartTime);
        setEndDate(new Date());
        setEndTime(calculatedEndTime || '17:25');
        setReminders([{ value: 30, unit: 'minutes' }]);
        const memberNames = selectedFamily?.members
            ?.filter(m => m && typeof m === 'object' && m.name)
            .map(m => m.name) || [];
        setParticipants(memberNames);
        setVisibleTo(memberNames);
        setEventColor('#1a73e8');
        setRepeatOption('none');
        setRepeatEndDate(null);
        setDescription('');
        setTimezone('Asia/Kolkata');
        setEditingEvent(null);
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        setEventTitle(event.title || '');
        setLocation(event.location || '');
        setIsAllDay(event.all_day || false);
        setStartDate(new Date(event.start_date));
        setStartTime(event.start_time || '16:25');
        setEndDate(event.end_date ? new Date(event.end_date) : new Date(event.start_date));
        setEndTime(event.end_time || '17:25');
        setEventColor(event.color || '#1a73e8');
        setRepeatOption(event.repeat_pattern || 'none');
        setRepeatEndDate(event.repeat_end_date || null);
        setDescription(event.description || '');
        setTimezone(event.timezone || 'Asia/Kolkata');
        
        // Parse reminders
        if (event.reminders && Array.isArray(event.reminders) && event.reminders.length > 0) {
            const parsedReminders = event.reminders.map(reminder => {
                if (typeof reminder === 'object' && reminder.time_before) {
                    const [value, unit] = reminder.time_before.split('_');
                    return { value: parseInt(value), unit: unit || 'minutes' };
                }
                return { value: 30, unit: 'minutes' };
            });
            setReminders(parsedReminders);
        } else {
            setReminders([{ value: 30, unit: 'minutes' }]);
        }
        
        // Parse participants and visible_to
        setParticipants(event.participants || []);
        setVisibleTo(event.visible_to || []);
        
        setShowAddEventModal(true);
    };

    const handleDeleteEvent = async (eventId) => {
        if (!selectedFamily) return;
        if (!window.confirm('Are you sure you want to delete this event?')) return;

        try {
            await deleteCalendarEvent(selectedFamily.id, eventId);
            setEvents(events.filter(e => e.id !== eventId));
        } catch (error) {
            console.error('Error deleting event:', error);
            alert(`Failed to delete event: ${error.message}`);
        }
    };

    const removeReminder = (index) => {
        setReminders(reminders.filter((_, i) => i !== index));
    };

    const addReminder = () => {
        setReminders([...reminders, { value: 30, unit: 'minutes' }]);
    };

    const updateReminder = (index, field, value) => {
        const updated = reminders.map((reminder, i) =>
            i === index ? { ...reminder, [field]: field === 'value' ? parseInt(value) : value } : reminder
        );
        setReminders(updated);
    };

    const toggleParticipant = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (participants.includes(memberName)) {
            setParticipants(participants.filter(m => m !== memberName));
        } else {
            setParticipants([...participants, memberName]);
        }
    };

    const toggleVisibility = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (visibleTo.includes(memberName)) {
            setVisibleTo(visibleTo.filter(m => m !== memberName));
        } else {
            setVisibleTo([...visibleTo, memberName]);
        }
    };

    const getFamilyMembers = () => {
        return selectedFamily?.members?.filter(m => m && typeof m === 'object' && m.name) || [];
    };

    // Calendar logic
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
    };

    const generateCalendarDays = () => {
        const { daysInMonth, startingDayOfWeek, firstDay } = getDaysInMonth(currentDate);
        const days = [];

        // Previous month's days
        const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                day: prevMonthDays - i,
                isCurrentMonth: false,
                date: new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, prevMonthDays - i)
            });
        }

        // Current month's days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                day: i,
                isCurrentMonth: true,
                date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i)
            });
        }

        // Next month's days
        const remainingDays = 42 - days.length; // 6 rows * 7 days
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                day: i,
                isCurrentMonth: false,
                date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i)
            });
        }

        return days;
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const isSelected = (date) => {
        return date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear();
    };

    const formatDate = (date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
    };

    const getNextDays = (count = 4) => {
        const days = [];
        const today = new Date();
        for (let i = 0; i < count; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            days.push(date);
        }
        return days;
    };

    const getDayLabel = (date) => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        if (isToday(date)) {
            return 'Today';
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return 'Tomorrow';
        } else {
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return days[date.getDay()];
        }
    };

    const getEventsForDate = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return events.filter(event => event.start_date === dateStr);
    };

    // Check if a date has events (for mini calendar dots)
    const hasEventsOnDate = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return events.some(event => event.start_date === dateStr);
    };

    // Generate hours for time-based views (24-hour format)
    const generateHours = () => {
        const hours = [];
        for (let i = 0; i < 24; i++) {
            hours.push(i);
        }
        return hours;
    };

    // Get events for a specific date and hour
    const getEventsForDateAndHour = (date, hour) => {
        const dateStr = date.toISOString().split('T')[0];
        return events.filter(event => {
            if (event.start_date !== dateStr) return false;
            if (event.all_day) return hour === 0; // Show all-day events at midnight
            if (!event.start_time) return false;
            const eventHour = parseInt(event.start_time.split(':')[0]);
            return eventHour === hour;
        });
    };

    // Get events that span across a time slot
    const getEventsInTimeSlot = (date, hour) => {
        const dateStr = date.toISOString().split('T')[0];
        return events.filter(event => {
            if (event.start_date !== dateStr) return false;
            if (event.all_day) return hour === 0;
            if (!event.start_time) return false;
            const startHour = parseInt(event.start_time.split(':')[0]);
            const endHour = event.end_time ? parseInt(event.end_time.split(':')[0]) : startHour + 1;
            return hour >= startHour && hour < endHour;
        });
    };

    // Calculate ISO week number for a date
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    // Generate mini calendar days for current month
    const generateMiniCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
        
        const days = [];
        const currentDateObj = new Date(startDate);
        
        // Generate 6 weeks (42 days)
        for (let i = 0; i < 42; i++) {
            days.push(new Date(currentDateObj));
            currentDateObj.setDate(currentDateObj.getDate() + 1);
        }
        
        return days;
    };

    // Get week numbers for mini calendar (one per week row)
    const getMiniCalendarWeekNumbers = () => {
        const days = generateMiniCalendarDays();
        const weekNumbers = [];
        // Get week number for the first day of each week (Sunday)
        for (let i = 0; i < 42; i += 7) {
            weekNumbers.push(getWeekNumber(days[i]));
        }
        return weekNumbers;
    };

    const hexToRgba = (hex, alpha = 0.15) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Scroll to current date when view changes
    const scrollContainerRef = useRef(null);
    
    // Get dates for current view (scrollable)
    const getDatesForView = () => {
        const dates = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        switch (viewType) {
            case 'day':
                // Generate 90 days before and after today for scrolling
                for (let i = -90; i <= 90; i++) {
                    const date = new Date(today);
                    date.setDate(today.getDate() + i);
                    dates.push(date);
                }
                break;
            case '3-day':
                // Generate 3-day groups for scrolling
                for (let group = -30; group <= 30; group++) {
                    const startDate = new Date(today);
                    startDate.setDate(today.getDate() + (group * 3));
                    for (let i = 0; i < 3; i++) {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + i);
                        dates.push(date);
                    }
                }
                break;
            case 'week':
                // Generate weeks for scrolling
                for (let week = -12; week <= 12; week++) {
                    const weekStart = new Date(today);
                    weekStart.setDate(today.getDate() + (week * 7));
                    const dayOfWeek = weekStart.getDay();
                    weekStart.setDate(weekStart.getDate() - dayOfWeek); // Start from Sunday
                    for (let i = 0; i < 7; i++) {
                        const date = new Date(weekStart);
                        date.setDate(weekStart.getDate() + i);
                        dates.push(date);
                    }
                }
                break;
            case 'month':
                // For month view, generate multiple months for scrolling
                const startMonth = new Date(today.getFullYear(), today.getMonth() - 6, 1); // 6 months ago
                const endMonth = new Date(today.getFullYear(), today.getMonth() + 6, 0); // 6 months ahead
                
                for (let month = new Date(startMonth); month <= endMonth; month.setMonth(month.getMonth() + 1)) {
                    const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
                    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
                    const firstDay = new Date(monthStart);
                    firstDay.setDate(firstDay.getDate() - firstDay.getDay()); // Start from Sunday
                    
                    for (let d = new Date(firstDay); d <= monthEnd || d.getDay() !== 0 || d.getDate() <= 7; d.setDate(d.getDate() + 1)) {
                        dates.push(new Date(d));
                        if (dates.length > 0 && dates.length % 42 === 0 && d.getDay() === 6) break; // End of week
                    }
                }
                break;
            case 'agenda':
                // Generate dates for agenda view (similar to old default view)
                // Generate 180 days before and after today for scrolling
                for (let i = -180; i <= 180; i++) {
                    const date = new Date(today);
                    date.setDate(today.getDate() + i);
                    dates.push(date);
                }
                break;
        }
        
        return dates;
    };
    
    // Scroll to today when component mounts or view changes
    useEffect(() => {
        if (scrollContainerRef.current) {
            setTimeout(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const todayKey = today.toISOString().split('T')[0];
                
                const element = scrollContainerRef.current?.querySelector(`[data-date-key="${todayKey}"]`);
                if (element) {
                    const container = scrollContainerRef.current;
                    
                    // Calculate scroll position using getBoundingClientRect for accurate positioning
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = element.getBoundingClientRect();
                    
                    // Current scroll position
                    const currentScrollTop = container.scrollTop;
                    
                    // Calculate element's position relative to container's scrollable content
                    const elementTopInContent = elementRect.top - containerRect.top + currentScrollTop;
                    
                    // For Day and 3-Day views: scroll to element position (no offset) - header at top
                    // For week view: account for the "Week of..." sticky header
                    // For agenda view: account for sticky month headers
                    let offset = 0;
                    
                    if (viewType === 'week') {
                        // Check if there's a week header above the date header
                        const weekHeader = element.closest('.space-y-8')?.querySelector('h3.sticky');
                        if (weekHeader) {
                            offset = weekHeader.offsetHeight + 16; // Header height + spacing
                        }
                    } else if (viewType === 'agenda') {
                        offset = 60; // Offset for agenda view sticky headers
                    }
                    // For 'day' and '3-day', offset remains 0
                    
                    // Calculate target scroll position
                    let targetScrollTop = elementTopInContent - offset;
                    
                    // Ensure we don't scroll to negative values
                    targetScrollTop = Math.max(0, targetScrollTop);
                    
                    requestAnimationFrame(() => {
                        container.scrollTo({
                            top: targetScrollTop,
                            behavior: 'smooth'
                        });
                    });
                }
            }, 300); // Increased timeout to ensure DOM is fully rendered
        }
    }, [viewType, currentDate]);

    const getViewLabel = () => {
        const labels = {
            'day': 'Day',
            '3-day': '3-Day',
            'week': 'Week',
            'month': 'Month',
            'agenda': 'Agenda'
        };
        return labels[viewType] || 'Month';
    };

    // Helper function to render an event
    const renderEvent = (event) => {
        const isGoogleCalendarEvent = event.source_calendar_id && event.google_calendar_link;
        const googleCalendar = googleCalendars.find(cal => cal.id === event.source_calendar_id);
        
        return (
            <div 
                key={event.id} 
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isGoogleCalendarEvent) {
                        handleEditEvent(event);
                    } else if (event.google_calendar_link) {
                        window.open(event.google_calendar_link, '_blank');
                    }
                }}
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors group relative hover:opacity-90 ${
                    isGoogleCalendarEvent ? 'cursor-pointer' : 'cursor-pointer'
                }`}
                style={{ 
                    backgroundColor: event.color ? hexToRgba(event.color, 0.15) : 'rgba(59, 130, 246, 0.1)',
                    borderLeft: `4px solid ${event.color || '#3b82f6'}`
                }}
            >
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{event.title}</p>
                        {isGoogleCalendarEvent && googleCalendar && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                                {googleCalendar.initials}
                            </span>
                        )}
                    </div>
                    {event.location && (
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Globe className="w-3 h-3" />
                            {event.location}
                        </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                        {event.all_day ? 'All day' : `${event.start_time || ''}${event.end_time ? ` - ${event.end_time}` : ''}`}
                    </p>
                    {isGoogleCalendarEvent && event.google_calendar_link && (
                        <a
                            href={event.google_calendar_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2"
                        >
                            <ExternalLink className="w-3 h-3" />
                            View in Google Calendar
                        </a>
                    )}
                    {event.participants && event.participants.length > 0 && !isGoogleCalendarEvent && (
                        <div className="flex items-center gap-1 mt-2">
                            <UsersIcon className="w-3 h-3 text-gray-400" />
                            <p className="text-xs text-gray-500">
                                {event.participants.length} participant{event.participants.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    )}
                </div>
                {!isGoogleCalendarEvent && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 rounded-lg transition-all text-red-600"
                        title="Delete event"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    };

    const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className="min-h-screen bg-secondary-gray">
            {/* Navbar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => handleFamilySelect(family)}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                            }`}
                                                    >
                                                        <img
                                                            src={family.photo}
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>

                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>

                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600 font-semibold"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Calendar Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb and Title */}
                <div className="mb-6">
                    <div className="mb-2 flex items-center gap-2 text-sm">
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="text-gray-600 hover:text-primary-blue transition-colors"
                        >
                            Dashboard
                        </button>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">Calendar</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <h1 className="text-3xl font-bold text-gray-900">
                            {selectedFamily ? `${selectedFamily.name} - Calendar` : 'Calendar'}
                        </h1>
                        <div className="flex items-center gap-3">
                            {/* View Dropdown */}
                            <div className="relative" ref={viewDropdownRef}>
                                <button
                                    onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
                                >
                                    <CalendarIcon className="w-4 h-4" />
                                    <span>{getViewLabel()}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${viewDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {viewDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {['day', '3-day', 'week', 'month', 'agenda'].map((view) => (
                                            <button
                                                key={view}
                                                onClick={() => {
                                                    setViewType(view);
                                                    setViewDropdownOpen(false);
                                                }}
                                                className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                                                    viewType === view ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                                                }`}
                                            >
                                                {view === '3-day' ? '3-Day' : view.charAt(0).toUpperCase() + view.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setShowSettings(true)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Calendar Settings"
                            >
                                <Settings className="w-6 h-6 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Navigation Controls */}
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                const newDate = new Date(currentDate);
                                if (viewType === 'day') {
                                    newDate.setDate(newDate.getDate() - 1);
                                } else if (viewType === '3-day') {
                                    newDate.setDate(newDate.getDate() - 3);
                                } else if (viewType === 'week') {
                                    newDate.setDate(newDate.getDate() - 7);
                                } else if (viewType === 'agenda') {
                                    newDate.setMonth(newDate.getMonth() - 1);
                                } else {
                                    newDate.setMonth(newDate.getMonth() - 1);
                                }
                                setCurrentDate(newDate);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Previous"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                            onClick={() => {
                                setCurrentDate(new Date());
                                setSelectedDate(new Date());
                            }}
                            className="px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium text-gray-700"
                        >
                            Today
                        </button>
                        <button
                            onClick={() => {
                                const newDate = new Date(currentDate);
                                if (viewType === 'day') {
                                    newDate.setDate(newDate.getDate() + 1);
                                } else if (viewType === '3-day') {
                                    newDate.setDate(newDate.getDate() + 3);
                                } else if (viewType === 'week') {
                                    newDate.setDate(newDate.getDate() + 7);
                                } else if (viewType === 'agenda') {
                                    newDate.setMonth(newDate.getMonth() + 1);
                                } else {
                                    newDate.setMonth(newDate.getMonth() + 1);
                                }
                                setCurrentDate(newDate);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Next"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                        <span className="ml-4 text-lg font-semibold text-gray-900">
                            {viewType === 'month' ? monthYear : 
                             viewType === 'agenda' ? monthYear :
                             viewType === 'week' ? `Week of ${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` :
                             viewType === '3-day' ? `${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(currentDate.getTime() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` :
                             currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                    </div>
                </div>

                {/* Calendar Layout: Mini Calendar + Main View */}
                <div className="flex gap-6">
                    {/* Mini Calendar - Left Side */}
                    <div className="flex-shrink-0 w-64">
                        <div className="bg-white rounded-xl shadow-md p-4 sticky top-4">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900">{monthYear}</h2>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    >
                                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    >
                                        <ChevronRight className="w-4 h-4 text-gray-600" />
                                    </button>
                                </div>
                            </div>
                            <div className={`grid gap-1 ${weekNumbersEnabled ? 'grid-cols-8' : 'grid-cols-7'}`}>
                                {/* Week number header (if enabled) */}
                                {weekNumbersEnabled && (
                                    <div className="text-center text-xs font-medium text-gray-500 py-1">
                                        Wk
                                    </div>
                                )}
                                {/* Day headers */}
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                                    <div key={index} className="text-center text-xs font-medium text-gray-500 py-1">
                                        {day}
                                    </div>
                                ))}
                                {/* Calendar days with week numbers */}
                                {generateMiniCalendarDays().map((date, index) => {
                                    const dateStr = date.toISOString().split('T')[0];
                                    const isTodayDate = isToday(date);
                                    const isSelectedDate = isSelected(date);
                                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                                    const hasEvents = hasEventsOnDate(date);
                                    const isFirstDayOfWeek = index % 7 === 0;
                                    const weekNumber = isFirstDayOfWeek ? getWeekNumber(date) : null;
                                    
                                    return (
                                        <>
                                            {/* Week number cell (only for first day of each week) */}
                                            {weekNumbersEnabled && isFirstDayOfWeek && (
                                                <div key={`wn-${index}`} className="flex items-center justify-center text-xs font-medium text-gray-600 bg-gray-100 rounded py-1">
                                                    {weekNumber}
                                                </div>
                                            )}
                                            {/* Date cell */}
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    setSelectedDate(date);
                                                    setCurrentDate(date);
                                                    // Scroll to selected date after state update
                                                    setTimeout(() => {
                                                        const dateStr = date.toISOString().split('T')[0];
                                                        const element = scrollContainerRef.current?.querySelector(`[data-date-key="${dateStr}"]`);
                                                        if (element) {
                                                            const container = scrollContainerRef.current;
                                                            const containerRect = container.getBoundingClientRect();
                                                            const elementRect = element.getBoundingClientRect();
                                                            const currentScrollTop = container.scrollTop;
                                                            const elementTopInContent = elementRect.top - containerRect.top + currentScrollTop;
                                                            
                                                            let offset = 0;
                                                            
                                                            if (viewType === 'week') {
                                                                const weekHeader = element.closest('.space-y-8')?.querySelector('h3.sticky');
                                                                if (weekHeader) {
                                                                    offset = weekHeader.offsetHeight + 16;
                                                                }
                                                            } else if (viewType === 'agenda') {
                                                                offset = 60;
                                                            }
                                                            // For 'day' and '3-day', offset remains 0
                                                            
                                                            let targetScrollTop = elementTopInContent - offset;
                                                            targetScrollTop = Math.max(0, targetScrollTop);
                                                            
                                                            requestAnimationFrame(() => {
                                                                container.scrollTo({
                                                                    top: targetScrollTop,
                                                                    behavior: 'smooth'
                                                                });
                                                            });
                                                        }
                                                    }, 150);
                                                }}
                                                className={`
                                                    aspect-square flex flex-col items-center justify-center rounded text-xs font-medium transition-all relative
                                                    ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                                                    ${isTodayDate ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                                                    ${isSelectedDate && !isTodayDate ? 'bg-gray-200' : ''}
                                                    ${!isTodayDate && !isSelectedDate && isCurrentMonth ? 'hover:bg-gray-100' : ''}
                                                `}
                                            >
                                                <span>{date.getDate()}</span>
                                                {hasEvents && (
                                                    <span className="absolute bottom-1 w-1 h-1 bg-red-500 rounded-full"></span>
                                                )}
                                            </button>
                                        </>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Main Calendar View - Right Side */}
                    <div className="flex-1 bg-white rounded-xl shadow-md overflow-hidden">
                        <div ref={scrollContainerRef} className="max-h-[calc(100vh-250px)] overflow-y-auto">
                            {viewType === 'agenda' ? (
                                // Agenda View - List dates vertically with events
                                (() => {
                                    const allDates = getDatesForView();
                                    const monthGroups = {};
                                    
                                    // Group dates by month for better organization
                                    allDates.forEach(date => {
                                        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                                        if (!monthGroups[monthKey]) {
                                            monthGroups[monthKey] = [];
                                        }
                                        monthGroups[monthKey].push(date);
                                    });
                                    
                                    return (
                                        <div className="p-4">
                                            {Object.keys(monthGroups).sort().map(monthKey => {
                                                const monthDates = monthGroups[monthKey];
                                                const firstDate = monthDates[0];
                                                const monthName = firstDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                                
                                                return (
                                                    <div key={monthKey} className="mb-8">
                                                        <h2 className="text-xl font-bold text-gray-900 mb-4 sticky top-0 bg-white py-2 z-10">
                                                            {monthName}
                                                        </h2>
                                                        <div className="space-y-4">
                                                            {monthDates.map((date) => {
                                                                const dayEvents = getEventsForDate(date);
                                                                const dateStr = date.toISOString().split('T')[0];
                                                                const isTodayDate = isToday(date);
                                                                const isSelectedDate = isSelected(date);
                                                                
                                                                return (
                                                                    <div key={dateStr} data-date-key={dateStr} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
                                                                        {/* Date Header */}
                                                                        <div className="flex items-center justify-between mb-3">
                                                                            <button
                                                                                onClick={() => {
                                                                                    resetEventForm();
                                                                                    setStartDate(date);
                                                                                    setEndDate(date);
                                                                                    setShowAddEventModal(true);
                                                                                }}
                                                                                className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors group w-full"
                                                                            >
                                                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold flex-shrink-0 ${
                                                                                    isTodayDate ? 'bg-blue-500 text-white' : 
                                                                                    isSelectedDate ? 'bg-gray-200 text-gray-900' : 
                                                                                    'bg-gray-100 text-gray-700 group-hover:bg-gray-200'
                                                                                }`}>
                                                                                    {date.getDate()}
                                                                                </div>
                                                                                <div className="text-left flex-1">
                                                                                    <p className={`text-sm font-medium ${
                                                                                        isTodayDate ? 'text-blue-600' : 'text-gray-600'
                                                                                    }`}>
                                                                                        {getDayLabel(date)}
                                                                                    </p>
                                                                                    <p className="text-lg font-semibold text-gray-900">
                                                                                        {formatDate(date)}
                                                                                    </p>
                                                                                </div>
                                                                                <Plus className="w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                            </button>
                                                                        </div>

                                                                        {/* Events for this date */}
                                                                        <div className="ml-14 space-y-2">
                                                                            {dayEvents.length > 0 ? (
                                                                                dayEvents.map((event) => renderEvent(event))
                                                                            ) : (
                                                                                <p className="text-sm text-gray-400 italic">No events</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()
                            ) : viewType === 'month' ? (
                                // Month Grid View - Grouped by month
                                (() => {
                                    const allDates = getDatesForView();
                                    const monthGroups = {};
                                    
                                    allDates.forEach(date => {
                                        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                                        if (!monthGroups[monthKey]) {
                                            monthGroups[monthKey] = [];
                                        }
                                        monthGroups[monthKey].push(date);
                                    });
                                    
                                    return (
                                        <div className="p-4">
                                            {Object.keys(monthGroups).sort().map(monthKey => {
                                                const monthDates = monthGroups[monthKey];
                                                const firstDate = monthDates[0];
                                                const monthName = firstDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                                
                                                return (
                                                    <div key={monthKey} className="mb-8">
                                                        <h2 className="text-xl font-bold text-gray-900 mb-4 sticky top-0 bg-white py-2 z-10">
                                                            {monthName}
                                                        </h2>
                                                        <div className="grid grid-cols-7 gap-1 mb-2">
                                                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                                                <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                                                                    {day}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="grid grid-cols-7 gap-1">
                                                            {monthDates.map((date, index) => {
                                                                const dateStr = date.toISOString().split('T')[0];
                                                                const isTodayDate = isToday(date);
                                                                const isCurrentMonth = date.getMonth() === parseInt(monthKey.split('-')[1]);
                                                                const dayEvents = getEventsForDate(date);
                                                                
                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        data-date-key={dateStr}
                                                                        className={`
                                                                            min-h-[100px] border border-gray-200 rounded p-2 cursor-pointer hover:bg-gray-50 transition-colors
                                                                            ${!isCurrentMonth ? 'bg-gray-50 opacity-50' : ''}
                                                                            ${isTodayDate ? 'border-blue-500 border-2' : ''}
                                                                        `}
                                                                        onClick={() => {
                                                                            resetEventForm();
                                                                            setStartDate(date);
                                                                            setEndDate(date);
                                                                            setShowAddEventModal(true);
                                                                        }}
                                                                    >
                                                                        <div className={`text-sm font-medium mb-1 ${isTodayDate ? 'text-blue-600' : 'text-gray-700'}`}>
                                                                            {date.getDate()}
                                                                        </div>
                                                                        <div className="space-y-1">
                                                                            {dayEvents.slice(0, 3).map((event) => (
                                                                                <div
                                                                                    key={event.id}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleEditEvent(event);
                                                                                    }}
                                                                                    className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                                                                                    style={{
                                                                                        backgroundColor: event.color ? hexToRgba(event.color, 0.3) : 'rgba(59, 130, 246, 0.2)',
                                                                                        color: event.color || '#3b82f6'
                                                                                    }}
                                                                                >
                                                                                    {event.all_day ? event.title : `${event.start_time || ''} ${event.title}`}
                                                                                </div>
                                                                            ))}
                                                                            {dayEvents.length > 3 && (
                                                                                <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()
                            ) : (
                                // Time-based views (Day, 3-Day, Week) - Grouped by view type
                                (() => {
                                    const allDates = getDatesForView();
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    
                                    // Group dates based on view type
                                    let dateGroups = [];
                                    if (viewType === 'day') {
                                        // Each day is its own group
                                        allDates.forEach(date => {
                                            dateGroups.push([date]);
                                        });
                                    } else if (viewType === '3-day') {
                                        // Group into sets of 3 days
                                        for (let i = 0; i < allDates.length; i += 3) {
                                            dateGroups.push(allDates.slice(i, i + 3));
                                        }
                                    } else if (viewType === 'week') {
                                        // Group into sets of 7 days (weeks)
                                        for (let i = 0; i < allDates.length; i += 7) {
                                            dateGroups.push(allDates.slice(i, i + 7));
                                        }
                                    }
                                    
                                    return (
                                        <div className="space-y-8">
                                            {dateGroups.map((groupDates, groupIndex) => {
                                                const firstDate = groupDates[0];
                                                const isCurrentGroup = groupDates.some(d => {
                                                    const dStr = d.toISOString().split('T')[0];
                                                    const todayStr = today.toISOString().split('T')[0];
                                                    return dStr === todayStr;
                                                });
                                                
                                                return (
                                                    <div key={groupIndex} className={isCurrentGroup ? '' : ''}>
                                                        {viewType === 'week' && (
                                                            <h3 className="text-lg font-semibold text-gray-700 mb-2 px-4 pt-4 sticky top-0 bg-white z-10">
                                                                Week of {firstDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                            </h3>
                                                        )}
                                                        <div className="flex">
                                                            {/* Time column */}
                                                            <div className="flex-shrink-0 w-20 border-r border-gray-200">
                                                                <div className="h-12 border-b border-gray-200"></div>
                                                                {generateHours().map((hour) => (
                                                                    <div key={hour} className="h-16 border-b border-gray-100 flex items-start justify-end pr-2 pt-1">
                                                                        <span className="text-xs text-gray-500">
                                                                            {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            
                                                            {/* Date columns */}
                                                            <div className="flex-1 flex">
                                                                {groupDates.map((date, dateIndex) => {
                                                                    const dateStr = date.toISOString().split('T')[0];
                                                                    const isTodayDate = isToday(date);
                                                                    const dayEvents = getEventsForDate(date);
                                                                    
                                                                    return (
                                                                        <div key={dateIndex} className="flex-1 border-r border-gray-200 last:border-r-0">
                                                                            {/* Date header */}
                                                                            <div 
                                                                                data-date-key={dateStr}
                                                                                className={`
                                                                                    h-12 border-b border-gray-200 flex items-center justify-center font-semibold cursor-pointer hover:bg-gray-50 transition-colors
                                                                                    ${isTodayDate ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}
                                                                                `}
                                                                                onClick={() => {
                                                                                    resetEventForm();
                                                                                    setStartDate(date);
                                                                                    setEndDate(date);
                                                                                    setShowAddEventModal(true);
                                                                                }}
                                                                            >
                                                                                <div className="text-center">
                                                                                    <div className="text-xs text-gray-500">{getDayLabel(date)}</div>
                                                                                    <div className="text-sm">{date.getDate()}</div>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            {/* Time slots */}
                                                                            <div className="relative">
                                                                                {generateHours().map((hour) => {
                                                                                    const hourEvents = getEventsInTimeSlot(date, hour);
                                                                                    return (
                                                                                        <div
                                                                                            key={hour}
                                                                                            className="h-16 border-b border-gray-100 relative cursor-pointer hover:bg-gray-50 transition-colors group"
                                                                                            onClick={() => {
                                                                                                resetEventForm();
                                                                                                const newDate = new Date(date);
                                                                                                newDate.setHours(hour, 0, 0, 0);
                                                                                                const startTimeStr = `${hour.toString().padStart(2, '0')}:00`;
                                                                                                const calculatedEndTime = calculateEndTime(startTimeStr, defaultEventDuration);
                                                                                                setStartDate(newDate);
                                                                                                setEndDate(newDate);
                                                                                                setStartTime(startTimeStr);
                                                                                                setEndTime(calculatedEndTime);
                                                                                                setIsAllDay(false);
                                                                                                setShowAddEventModal(true);
                                                                                            }}
                                                                                        >
                                                                                            {hourEvents.map((event, eventIndex) => (
                                                                                                <div
                                                                                                    key={event.id}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleEditEvent(event);
                                                                                                    }}
                                                                                                    className="absolute left-1 right-1 rounded px-2 py-1 text-xs font-medium cursor-pointer hover:opacity-80 z-10 overflow-hidden"
                                                                                                    style={{
                                                                                                        backgroundColor: event.color || '#3b82f6',
                                                                                                        color: 'white',
                                                                                                        top: eventIndex * 20,
                                                                                                        height: '18px'
                                                                                                    }}
                                                                                                >
                                                                                                    {event.all_day ? event.title : `${event.start_time || ''} ${event.title}`}
                                                                                                </div>
                                                                                            ))}
                                                                                            <div className="opacity-0 group-hover:opacity-100 absolute inset-0 flex items-center justify-center">
                                                                                                <Plus className="w-4 h-4 text-gray-400" />
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-around">
                    <button className="flex flex-col items-center gap-1 text-blue-500">
                        <CalendarIcon className="w-6 h-6" />
                        <span className="text-xs font-medium">Today</span>
                    </button>
                    <button
                        onClick={() => {
                            resetEventForm();
                            setShowAddEventModal(true);
                        }}
                        className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-500"
                    >
                        <Plus className="w-6 h-6" />
                        <span className="text-xs font-medium">Add</span>
                    </button>
                    <button className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-500">
                        <Filter className="w-6 h-6" />
                        <span className="text-xs font-medium">Filtered</span>
                    </button>
                </div>
            </div>

            {/* Add Event Modal */}
            {showAddEventModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
                    <div className="bg-gray-900 w-full md:max-w-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="bg-blue-600 px-4 py-4 flex items-center justify-between sticky top-0 z-10">
                            <button
                                onClick={() => {
                                    resetEventForm();
                                    setShowAddEventModal(false);
                                }}
                                className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                            <h2 className="text-xl font-semibold text-white">
                                {editingEvent ? 'Edit event' : 'Add an event'}
                            </h2>
                            <div className="flex items-center gap-2">
                                {editingEvent && (
                                    <button
                                        onClick={async () => {
                                            if (window.confirm('Are you sure you want to delete this event?')) {
                                                await handleDeleteEvent(editingEvent.id);
                                                resetEventForm();
                                                setShowAddEventModal(false);
                                            }
                                        }}
                                        className="p-2 hover:bg-red-600 rounded-lg transition-colors"
                                        title="Delete event"
                                    >
                                        <Trash2 className="w-5 h-5 text-white" />
                                    </button>
                                )}
                                <button
                                    onClick={handleSaveEvent}
                                    className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                                >
                                    <Check className="w-6 h-6 text-white" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="p-4 space-y-4">
                            {/* Title Input */}
                            <div>
                                <input
                                    type="text"
                                    value={eventTitle}
                                    onChange={(e) => setEventTitle(e.target.value)}
                                    placeholder="Title"
                                    className="w-full bg-gray-800 text-white placeholder-gray-500 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* All-day Toggle */}
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-gray-400" />
                                    <span className="text-gray-400">All-day</span>
                                </div>
                                <button
                                    onClick={() => {
                                        const newIsAllDay = !isAllDay;
                                        setIsAllDay(newIsAllDay);
                                        // When turning off all-day, recalculate end time based on start time + default duration
                                        if (!newIsAllDay && startTime) {
                                            const calculatedEndTime = calculateEndTime(startTime, defaultEventDuration);
                                            setEndTime(calculatedEndTime);
                                        }
                                    }}
                                    className={`w-14 h-8 rounded-full transition-colors relative ${isAllDay ? 'bg-blue-600' : 'bg-gray-600'
                                        }`}
                                >
                                    <div
                                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${isAllDay ? 'right-1' : 'left-1'
                                            }`}
                                    />
                                </button>
                            </div>

                            {/* Start Date and Time */}
                            <div className="flex items-center gap-3">
                                <div className="text-gray-400">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <input
                                    type="date"
                                    value={startDate.toISOString().split('T')[0]}
                                    onChange={(e) => setStartDate(new Date(e.target.value))}
                                    className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {!isAllDay && (
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => {
                                            const newStartTime = e.target.value;
                                            setStartTime(newStartTime);
                                            // Auto-calculate end time based on default duration
                                            if (!isAllDay && newStartTime) {
                                                const calculatedEndTime = calculateEndTime(newStartTime, defaultEventDuration);
                                                setEndTime(calculatedEndTime);
                                            }
                                        }}
                                        className="bg-gray-800 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                )}
                            </div>

                            {/* End Date and Time */}
                            <div className="flex items-center gap-3">
                                <div className="text-gray-400">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M5 12h14M12 5v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <input
                                    type="date"
                                    value={endDate.toISOString().split('T')[0]}
                                    onChange={(e) => setEndDate(new Date(e.target.value))}
                                    className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {!isAllDay && (
                                    <input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        className="bg-gray-800 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                )}
                            </div>

                            {/* Timezone */}
                            <div className="flex items-center gap-3 py-2">
                                <div className="text-gray-400">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                        <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" strokeWidth="2" />
                                    </svg>
                                </div>
                                <select
                                    value={timezone}
                                    onChange={(e) => setTimezone(e.target.value)}
                                    className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {timezones.map((tz) => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="border-t border-gray-700 my-4"></div>

                            {/* Participants */}
                            <button
                                onClick={() => setShowParticipantsModal(true)}
                                className="w-full flex items-center gap-3 py-3 hover:bg-gray-800 rounded-lg px-3 transition-colors"
                            >
                                <UsersIcon className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-400">Participants:</span>
                                <span className="text-blue-500">
                                    {participants.length === getFamilyMembers().length
                                        ? 'Everyone'
                                        : `${participants.length} member${participants.length !== 1 ? 's' : ''}`}
                                </span>
                                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                            </button>

                            {/* Visible to */}
                            <button
                                onClick={() => setShowVisibilityModal(true)}
                                className="w-full flex items-center gap-3 py-3 hover:bg-gray-800 rounded-lg px-3 transition-colors"
                            >
                                <Eye className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-400">Visible to:</span>
                                <span className="text-blue-500">
                                    {visibleTo.length === getFamilyMembers().length
                                        ? 'Everyone'
                                        : `${visibleTo.length} member${visibleTo.length !== 1 ? 's' : ''}`}
                                </span>
                                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                            </button>

                            {/* Change color */}
                            <button
                                onClick={() => setShowColorPicker(!showColorPicker)}
                                className="w-full flex items-center gap-3 py-3 hover:bg-gray-800 rounded-lg px-3 transition-colors"
                            >
                                <div className="w-6 h-6 rounded-full" style={{ backgroundColor: eventColor }}></div>
                                <span className="text-gray-400">Change color</span>
                                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                            </button>

                            {showColorPicker && (
                                <div className="grid grid-cols-6 gap-3 px-3 py-3 bg-gray-800 rounded-lg">
                                    {eventColors.map((colorOption) => (
                                        <button
                                            key={colorOption.color}
                                            onClick={() => {
                                                setEventColor(colorOption.color);
                                                setShowColorPicker(false);
                                            }}
                                            className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${eventColor === colorOption.color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''
                                                }`}
                                            style={{ backgroundColor: colorOption.color }}
                                            title={colorOption.name}
                                        >
                                            {eventColor === colorOption.color && (
                                                <Check className="w-5 h-5 text-white mx-auto" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Repeat */}
                            <button
                                onClick={() => setShowRepeatModal(true)}
                                className="w-full flex items-center gap-3 py-3 hover:bg-gray-800 rounded-lg px-3 transition-colors"
                            >
                                <Repeat className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-400">Repeat:</span>
                                <span className="text-blue-500">
                                    {repeatOptions.find(opt => opt.value === repeatOption)?.label || 'Does not repeat'}
                                </span>
                                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                            </button>

                            {/* Description */}
                            <div className="px-3">
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Add description or notes..."
                                    rows="4"
                                    className="w-full bg-gray-800 text-white placeholder-gray-500 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            {/* Location Input */}
                            <div className="px-3">
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="Add location"
                                    className="w-full bg-gray-800 text-white placeholder-gray-500 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="border-t border-gray-700 my-4"></div>

                            {/* Reminders */}
                            <div className="space-y-3 px-3">
                                {reminders.map((reminder, index) => (
                                    <div key={index} className="flex items-center gap-3 py-2">
                                        <Bell className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <input
                                            type="number"
                                            value={reminder.value}
                                            onChange={(e) => updateReminder(index, 'value', e.target.value)}
                                            min="1"
                                            className="w-20 bg-gray-800 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <select
                                            value={reminder.unit}
                                            onChange={(e) => updateReminder(index, 'unit', e.target.value)}
                                            className="flex-1 bg-gray-800 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {reminderUnits.map((unit) => (
                                                <option key={unit.value} value={unit.value}>{unit.label} before</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => removeReminder(index)}
                                            className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
                                        >
                                            <X className="w-5 h-5 text-gray-400" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={addReminder}
                                    className="text-blue-500 hover:text-blue-400 transition-colors py-2 text-sm font-medium"
                                >
                                    + Add another reminder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Participants Modal */}
            {showParticipantsModal && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-gray-900 rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                        <div className="bg-blue-600 px-4 py-4 flex items-center justify-between sticky top-0">
                            <h3 className="text-xl font-semibold text-white">Select Participants</h3>
                            <button
                                onClick={() => setShowParticipantsModal(false)}
                                className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>
                        <div className="p-4 space-y-2">
                            {getFamilyMembers().map((member, index) => (
                                <label
                                    key={index}
                                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={participants.includes(member.name)}
                                        onChange={() => toggleParticipant(member)}
                                        className="w-5 h-5 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <MemberAvatar name={member.name} photo={member.photo} size="sm" />
                                    <span className="text-white">{member.name}</span>
                                </label>
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-700">
                            <button
                                onClick={() => setShowParticipantsModal(false)}
                                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Visibility Modal */}
            {showVisibilityModal && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-gray-900 rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                        <div className="bg-blue-600 px-4 py-4 flex items-center justify-between sticky top-0">
                            <h3 className="text-xl font-semibold text-white">Visible To</h3>
                            <button
                                onClick={() => setShowVisibilityModal(false)}
                                className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>
                        <div className="p-4 space-y-2">
                            {getFamilyMembers().map((member, index) => (
                                <label
                                    key={index}
                                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={visibleTo.includes(member.name)}
                                        onChange={() => toggleVisibility(member)}
                                        className="w-5 h-5 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <MemberAvatar name={member.name} photo={member.photo} size="sm" />
                                    <span className="text-white">{member.name}</span>
                                </label>
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-700">
                            <button
                                onClick={() => setShowVisibilityModal(false)}
                                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Repeat Modal */}
            {showRepeatModal && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-gray-900 rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                        <div className="bg-blue-600 px-4 py-4 flex items-center justify-between sticky top-0">
                            <h3 className="text-xl font-semibold text-white">Repeat Event</h3>
                            <button
                                onClick={() => setShowRepeatModal(false)}
                                className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>
                        <div className="p-4 space-y-2">
                            {repeatOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        setRepeatOption(option.value);
                                        if (option.value === 'none') {
                                            setRepeatEndDate(null);
                                        }
                                    }}
                                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${repeatOption === option.value
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-800 text-gray-300 hover:bg-gray-750'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span>{option.label}</span>
                                        {repeatOption === option.value && (
                                            <Check className="w-5 h-5" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                        {repeatOption !== 'none' && (
                            <div className="p-4 border-t border-gray-700">
                                <label className="block text-gray-400 text-sm mb-2">Repeat ends (optional)</label>
                                <input
                                    type="date"
                                    value={repeatEndDate || ''}
                                    onChange={(e) => setRepeatEndDate(e.target.value)}
                                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        )}
                        <div className="p-4 border-t border-gray-700">
                            <button
                                onClick={() => setShowRepeatModal(false)}
                                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>

                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}

            {/* Calendar Settings Modal */}
            <CalendarSettings 
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                selectedFamily={selectedFamily}
            />
        </div>
    );
};

export default Calendar;

